import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, Gauge, Loader2, RefreshCw, Users } from "lucide-react";
import { TaskService } from "./TaskService";
import { filterTasksForUser } from "../../utils/taskFilters";

const DAY_MS = 24 * 60 * 60 * 1000;

const displayUser = (user) =>
  user?.fullName || user?.name || user?.displayName || user?.email || user?.id || "User";

const getUserPhoto = (user) =>
  user?.photoURL ||
  user?.photoUrl ||
  user?.profilePhotoUrl ||
  user?.profilePhotoURL ||
  user?.profileImageUrl ||
  user?.avatarUrl ||
  user?.avatarURL ||
  user?.photo?.url ||
  "";

const getInitials = (value) => {
  const text = String(value || "").trim();
  if (!text) return "U";
  const parts = text.split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const toDate = (raw) => {
  if (!raw) return null;
  if (typeof raw?.toDate === "function") return raw.toDate();
  if (raw instanceof Date) return raw;
  if (typeof raw === "string") {
    const next = new Date(raw);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  if (typeof raw === "number") {
    const next = new Date(raw);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  return null;
};

const isClosedTask = (task) => {
  const status = String(task?.status || "To Do");
  return status === "Completed" || status === "Cancelled";
};

const isOverdueTask = (task, now = Date.now()) => {
  if (isClosedTask(task)) return false;
  const due = toDate(task?.dueAt);
  return due ? due.getTime() < now : false;
};

const isDueSoonTask = (task, now = Date.now()) => {
  if (isClosedTask(task)) return false;
  const due = toDate(task?.dueAt);
  if (!due) return false;
  const diff = due.getTime() - now;
  return diff >= 0 && diff <= 3 * DAY_MS;
};

const getAssignedIds = (task) => {
  const direct = Array.isArray(task?.assignedToUserIds) ? task.assignedToUserIds.filter(Boolean) : [];
  if (direct.length) return direct;
  const memberIds = Array.isArray(task?.memberIds) ? task.memberIds.filter(Boolean) : [];
  if (memberIds.length) return memberIds;
  return [];
};

const getLoadTone = (workload) => {
  if (workload.overdue > 0) return "bg-rose-500";
  if (workload.dueSoon > 2) return "bg-amber-500";
  if (workload.open > 5) return "bg-sky-500";
  return "bg-emerald-500";
};

export default function TaskWorkload({
  companyId,
  me,
  usersMap = {},
  groupsMap = {},
  onEdit,
  onRefresh,
}) {
  const [rawTasks, setRawTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!companyId) return;
      setLoading(true);
      setError("");
      try {
        const all = await TaskService.getCompanyTasks(companyId, { me });
        if (!cancelled) setRawTasks(Array.isArray(all) ? all : []);
      } catch (err) {
        if (!cancelled) {
          console.error("[TaskWorkload] load failed:", err);
          setError(err?.message || "Failed to load tasks.");
          setRawTasks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, me]);

  const tasks = useMemo(() => filterTasksForUser(rawTasks, me), [rawTasks, me]);

  const normalizedUsersMap = useMemo(() => {
    const map = {};
    Object.entries(usersMap || {}).forEach(([key, user]) => {
      if (!user) return;
      map[key] = user;
      if (user.uid) map[user.uid] = user;
      if (user.id) map[user.id] = user;
    });
    return map;
  }, [usersMap]);

  const workloadRows = useMemo(() => {
    const map = new Map();
    const now = Date.now();

    const ensure = (id, label, icon = "user", user = null) => {
      if (!map.has(id)) {
        map.set(id, {
          id,
          label,
          icon,
          photoUrl: getUserPhoto(user),
          initials: getInitials(label),
          open: 0,
          overdue: 0,
          dueSoon: 0,
          completed: 0,
          estimatedHours: 0,
          spotlight: [],
        });
      } else if (user && !map.get(id).photoUrl) {
        map.get(id).photoUrl = getUserPhoto(user);
      }
      return map.get(id);
    };

    tasks.forEach((task) => {
      const assignedIds = getAssignedIds(task);
      const targets =
        assignedIds.length > 0
          ? assignedIds.map((id) => ({
              id,
              label: displayUser(normalizedUsersMap[id]),
              icon: "user",
              user: normalizedUsersMap[id],
            }))
          : task?.groupId
          ? [
              {
                id: `group:${task.groupId}`,
                label: groupsMap[task.groupId]?.name || task?.groupName || "Group",
                icon: "group",
              },
            ]
          : [{ id: "unassigned", label: "Unassigned", icon: "user" }];

      targets.forEach((target) => {
        const row = ensure(target.id, target.label, target.icon, target.user || null);
        const closed = isClosedTask(task);
        if (closed) row.completed += 1;
        else row.open += 1;
        if (isOverdueTask(task, now)) row.overdue += 1;
        if (isDueSoonTask(task, now)) row.dueSoon += 1;
        row.estimatedHours += Math.max(0, Number(task?.estimatedHours || 0));

        if (row.spotlight.length < 4 && !closed) {
          row.spotlight.push(task);
        }
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      if (b.overdue !== a.overdue) return b.overdue - a.overdue;
      if (b.open !== a.open) return b.open - a.open;
      return a.label.localeCompare(b.label);
    });
  }, [tasks, normalizedUsersMap, groupsMap]);

  const totals = useMemo(() => {
    return workloadRows.reduce(
      (acc, row) => {
        acc.open += row.open;
        acc.overdue += row.overdue;
        acc.dueSoon += row.dueSoon;
        acc.people += 1;
        acc.completed += row.completed;
        return acc;
      },
      { open: 0, overdue: 0, dueSoon: 0, people: 0, completed: 0 }
    );
  }, [workloadRows]);

  const capacityReference = useMemo(() => {
    const activeBase = totals.open + totals.completed;
    const activePct = activeBase > 0 ? Math.round((totals.open / activeBase) * 100) : 0;
    return {
      activePct,
      label: "Active load = open tasks divided by all assigned tasks in this view.",
    };
  }, [totals]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const all = await TaskService.getCompanyTasks(companyId, { me });
      setRawTasks(Array.isArray(all) ? all : []);
      if (typeof onRefresh === "function") await onRefresh();
    } catch (err) {
      console.error("[TaskWorkload] refresh failed:", err);
      setError(err?.message || "Failed to refresh tasks.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700">
        <div className="flex items-center gap-3 text-sm font-medium">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading workload…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!workloadRows.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <Gauge className="mx-auto h-8 w-8 text-slate-300" />
        <div className="mt-3 text-sm font-semibold text-slate-900">No task workload yet</div>
        <div className="mt-1 text-sm text-slate-500">
          Once tasks are assigned, this will show who is carrying what.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-5">
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Open</div>
          <div className="mt-2 text-2xl font-semibold text-slate-900">{totals.open}</div>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-rose-700">Overdue</div>
          <div className="mt-2 text-2xl font-semibold text-rose-900">{totals.overdue}</div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-amber-700">Due Soon</div>
          <div className="mt-2 text-2xl font-semibold text-amber-900">{totals.dueSoon}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Lanes</div>
          <div className="mt-2 text-2xl font-semibold text-slate-900">{totals.people}</div>
        </div>
        <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-sky-700">Capacity Ref</div>
          <div className="mt-2 text-2xl font-semibold text-sky-900">{capacityReference.activePct}%</div>
          <div className="mt-1 text-[11px] leading-4 text-sky-800">{capacityReference.label}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Workload</div>
            <div className="text-xs text-slate-500">Who is overloaded, who is due soon, and what needs attention next.</div>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="divide-y divide-slate-200">
          {workloadRows.map((row) => {
            const totalLoad = Math.max(1, row.open + row.completed);
            const openPct = Math.min(100, Math.round((row.open / totalLoad) * 100));
            return (
              <div key={row.id} className="grid gap-4 px-4 py-4 lg:grid-cols-[260px,1fr,320px] lg:items-center">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {row.icon === "group" ? (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                        <Users className="h-4 w-4" />
                      </div>
                    ) : (
                      row.photoUrl ? (
                        <img
                          src={row.photoUrl}
                          alt={row.label}
                          className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
                          {row.initials || getInitials(row.label)}
                        </div>
                      )
                    )}
                    <div className="truncate text-sm font-semibold text-slate-900">{row.label}</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{row.open} open</span>
                    <span className="rounded-full bg-rose-100 px-2.5 py-1 text-rose-700">{row.overdue} overdue</span>
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700">{row.dueSoon} due soon</span>
                    {row.estimatedHours > 0 ? (
                      <span className="rounded-full bg-sky-100 px-2.5 py-1 text-sky-700">
                        {row.estimatedHours}h planned
                      </span>
                    ) : null}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                    <span>Capacity load</span>
                    <span>{openPct}% active</span>
                  </div>
                  <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${getLoadTone(row)}`}
                      style={{ width: `${Math.max(8, openPct)}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  {row.spotlight.length ? (
                    row.spotlight.map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => onEdit?.(task)}
                        className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">{task.title || "Untitled task"}</div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {task.status || "To Do"}
                            {task?.dueAt ? ` • due ${new Date(task.dueAt).toLocaleDateString()}` : ""}
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-slate-400">No active spotlight tasks.</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
